import { EditorDialog, ThemeProvider } from '@sparemin/blockhead';
import React, { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { SegmentedVideoPlayerInstance } from 'components/SegmentedVideoPlayer';
import useOnChange from 'hooks/useOnChange';
import {
  aspectRatioSelector,
  createLayoutSegmentBackfillSelector,
  createLayoutSegmentCaptionOverrideSelector,
  createLayoutSegmentSlotsSelector,
  videoDimensionsSelector,
} from 'redux/modules/embed/selectors';
import EditSceneControlPanel from './EditSceneControlPanel';
import { EditSceneModalPlaybackProvider } from './EditSceneModalPlaybackContext';
import { EditSceneModalSlotsOverlayProvider } from './EditSceneModalSlotsOverlayContext/EditSceneModalSlotsOverlayContext';
import { EditSceneModalZoomProvider } from './EditSceneModalZoomContext';
import EditScenePreview from './EditScenePreview';
import EditScenePreviewController from './EditScenePreviewController';
import EditSceneZoomSlider from './EditSceneZoomSlider';
import {
  EditSceneModalStateProvider,
  useEditSceneModalStateProvider,
} from './state';
import useEditSceneModal from './useEditSceneModal';
import { block } from './utils';

const EditSceneModal: React.FC = () => {
  const { show, onExited, onHide, videoId, segmentId } = useEditSceneModal();
  const videoPlayerRef = useRef<SegmentedVideoPlayerInstance>();

  const videoDimensions = useSelector(videoDimensionsSelector(videoId));

  const modalState = useEditSceneModalStateProvider();
  const { dispatch } = modalState;

  const aspectRatio = useSelector(aspectRatioSelector);

  const defaultSlots = useSelector(
    createLayoutSegmentSlotsSelector(videoId, segmentId),
  );

  const backfill = useSelector(
    createLayoutSegmentBackfillSelector(videoId, segmentId),
  );

  const captionsOverride = useSelector(
    createLayoutSegmentCaptionOverrideSelector(videoId, segmentId),
  );

  const handlePreviewDimensionsChange = useCallback(
    (width: number, height: number) => {
      dispatch({
        type: 'preview-resize',
        payload: { width, height },
      });
    },
    [dispatch],
  );

  const handleExited = useCallback(() => {
    onExited?.();
    dispatch({ type: 'exit' });
  }, [dispatch, onExited]);

  useOnChange(show, {
    onChange(newValue) {
      if (newValue) {
        dispatch({
          type: 'open',
          payload: {
            backfill,
            captionsOverride,
            defaultSlots,
            videoDimensions,
          },
        });
      }
    },
  });

  return (
    <ThemeProvider theme="dark">
      <EditSceneModalStateProvider {...modalState}>
        <EditSceneModalZoomProvider>
          <EditSceneModalSlotsOverlayProvider>
            <EditSceneModalPlaybackProvider playerRef={videoPlayerRef}>
              <EditorDialog
                className={block()}
                aspectRatio={aspectRatio}
                controlPaneContent={<EditSceneControlPanel />}
                onPreviewDimensionsChange={handlePreviewDimensionsChange}
                previewPaneAccessories={{
                  before: <EditSceneZoomSlider />,
                  after: <EditScenePreviewController />,
                }}
                previewPaneContent={<EditScenePreview />}
                isDismissable
                isOpen={show}
                onClose={onHide}
                onExited={handleExited}
              />
            </EditSceneModalPlaybackProvider>
          </EditSceneModalSlotsOverlayProvider>
        </EditSceneModalZoomProvider>
      </EditSceneModalStateProvider>
    </ThemeProvider>
  );
};

export default EditSceneModal;
